import { isSeatLocked } from "@components/table/members/assign-seats/assign-seats-utils";
import { SeatCheckbox } from "@components/table/members/assign-seats/seat-checkbox";
import { useChangeSeats } from "@components/table/members/assign-seats/use-change-seats";
import { useMemberSeats } from "@components/table/members/assign-seats/use-member-seats";
import { useSeatsAvailable } from "@components/table/members/assign-seats/use-seats-available";
import { RemoveXgSeatCheck } from "@components/table/members/remove-xg-seat/remove-xg-seat-check";
import { RemoveXgSeatDialog } from "@components/table/members/remove-xg-seat/remove-xg-seat-dialog";
import { hasRolesToResolve } from "@components/table/members/remove-xg-seat/remove-xg-seat-utils";
import { useRemoveXgSeat } from "@components/table/members/remove-xg-seat/use-remove-xg-seat";
import { CheckboxGroup, Dialog } from "@flat-ui";
import { getPrettyName } from "@utils/user-utils";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { Stack } from "@mui-wrapper";
import { useState } from "react";

/** Whether a seat change takes the Sphere XG seat away from the member */
function isXgSeatRemoval({
  seat,
  isHeld
}) {
  return seat === "xgSeat" && isHeld;
}

/** The label of the action, so that whatever opens the dialog repeats what was clicked */
export const MANAGE_SEATS_TITLE = "Manage seats";
/**
 * Dialog that lets the user pick the seats of the workspace subscription that a member should hold.
 * Checking a seat gives it to the member, unchecking a seat they hold frees it again.
 *
 * Unchecking the Sphere XG seat does not free it right away: once every other seat changed, the
 * dialog looks up whether the member holds editor roles that need the seat. When they do,
 * RemoveXgSeatDialog walks the user through taking those away first, else the seat is freed at once.
 *
 * Every seat the workspace offers is listed, including the ones that cannot be changed, so that the
 * user learns why instead of wondering where they went.
 */
export function ManageSeatsDialog({
  member,
  isOpen,
  onClose
}) {
  const {
    seatOptions
  } = useMemberSeats(member);
  const {
    changeSeats
  } = useChangeSeats();
  const {
    removeXgSeat
  } = useRemoveXgSeat();
  const seatsAvailable = useSeatsAvailable();

  // The seats the user checked or unchecked, which are shown as the member holds them otherwise
  const [checkedSeats, setCheckedSeats] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingXgSeat, setIsCheckingXgSeat] = useState(false);
  // The steps to take the Sphere XG seat away, once the member turned out to hold roles that need it
  const [xgSeatSteps, setXgSeatSteps] = useState();

  // Only the seats that can still be changed count, so that a seat which changed meanwhile, e.g. by
  // the part of a save that succeeded, is not requested again
  const changedOptions = seatOptions.filter(option => !isSeatLocked(option) && isSeatChecked(option) !== option.isHeld);
  if (isOpen && xgSeatSteps) {
    return <RemoveXgSeatDialog member={member} steps={xgSeatSteps} onClose={closeDialog} />;
  }
  return <Dialog title={MANAGE_SEATS_TITLE} confirmText="Save" open={isOpen} onConfirm={saveSeats} showSpinner={isLoading} isConfirmDisabled={!changedOptions.length} onCancel={closeDialog} dataTestId="manage-seats-dialog">
      <CheckboxGroup label={<Stack component="span" sx={{
      gap: 1
    }}>
            Choose the seats for
            <Stack direction="row" component="span" sx={{
        alignItems: "center",
        gap: 1
      }}>
              <UserAvatarWithMenu member={member} />
              <strong>
                <var>{getPrettyName(member)}</var>
              </strong>
            </Stack>
          </Stack>}
    // The same space between the question and the seats as the dialog keeps around its title
    checkboxGroupSx={{
      gap: 1,
      mt: 2
    }}>
        {seatOptions.map(option_0 => <SeatCheckbox key={option_0.seat} option={option_0} isChecked={isSeatChecked(option_0)} seatsAvailable={seatsAvailable[option_0.seat]} onChange={(seat, isChecked) => setCheckedSeats(seats => ({
        ...seats,
        [seat]: isChecked
      }))} />)}
      </CheckboxGroup>

      {isCheckingXgSeat && <RemoveXgSeatCheck member={member} onStepsFound={steps => void continueXgSeatRemoval(steps)} />}
    </Dialog>;

  /** Whether the member should hold the seat once the dialog is saved */
  function isSeatChecked({
    seat: seat_0,
    isHeld
  }) {
    return checkedSeats[seat_0] ?? isHeld;
  }

  /** Closes the dialog without changing the seats of the member any further */
  function closeDialog() {
    setIsLoading(false);
    setIsCheckingXgSeat(false);
    setXgSeatSteps(undefined);
    setCheckedSeats({});
    onClose();
  }

  /**
   * Gives the member the checked seats and frees the unchecked ones they hold. The dialog stays open
   * when one of them could not be changed, so that the user can try that one again.
   *
   * Taking the Sphere XG seat away only starts once all the other seats changed, by looking up the
   * roles of the member that need it while the dialog still shows that it is saving.
   */
  async function saveSeats() {
    setIsLoading(true);
    const shouldRemoveXgSeat = changedOptions.some(isXgSeatRemoval);

    // All in one request, since separate requests at the same time would only keep one of them
    const seatChanges = changedOptions.filter(option_1 => !isXgSeatRemoval(option_1)).map(getSeatChange);
    if (!(await changeSeats(member, seatChanges))) {
      setIsLoading(false);
      return;
    }
    if (shouldRemoveXgSeat) {
      setIsCheckingXgSeat(true);
      return;
    }
    closeDialog();
  }

  /**
   * Walks the user through the steps to take the Sphere XG seat away, or takes it away at once when
   * the member holds no role that needs it. The dialog stays open when that failed, so that the
   * user can try again.
   */
  async function continueXgSeatRemoval(steps_0) {
    setIsCheckingXgSeat(false);
    if (hasRolesToResolve(steps_0)) {
      setIsLoading(false);
      setXgSeatSteps(steps_0);
      return;
    }
    if (await removeXgSeat(member)) {
      closeDialog();
    } else {
      setIsLoading(false);
    }
  }

  /** Returns the change that gives the member a seat they do not hold, or frees their SCENE seat */
  function getSeatChange({
    seat: seat_1,
    isHeld: isHeld_0
  }) {
    return isHeld_0 ? {
      seat: "sceneSeat",
      isAssigning: false
    } : {
      seat: seat_1,
      isAssigning: true
    };
  }
}